import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { useAuthUser } from "../contexts/AuthUserContext";
import { useLocalization } from "../contexts/LocalizationContext";
import {
  BarChart3,
  Bug,
  ChevronDown,
  ChevronUp,
  Headset,
  HelpCircle,
  Instagram,
  Linkedin,
  Link2,
  Receipt,
  Star,
  Tag,
  UserCircle,
} from "lucide-react";
import AppSideRail from "./AppSideRail";
import AppRightRail from "./AppRightRail";
import AppShellHeader from "./AppShellHeader";
import ComingSoonModal from "./ComingSoonModal";
import GuideTourOverlay from "./GuideTourOverlay";
import { useShop } from "../contexts/ShopContext";
import { GuideTourContext } from "../contexts/GuideTourContext";
import { useSupportCenter } from "../contexts/SupportCenterContext";
import {
  GET_AI_GREETING,
  GET_NOTIFICATIONS,
  GET_PRIMARY_TASK_PROGRESS,
  GET_SHOP_DATA_FOR_RECOMMENDATION,
} from "../graphql/queries";
import { MARK_NOTIFICATION_READ } from "../graphql/mutations";
import {
  createChannelChatSessionId,
  getChannelChatSessions,
} from "../utils/api/channelChatApi";
import styles from "./AppShell.module.css";

const normalizePathname = (pathname = "/") => {
  const raw = String(pathname || "").trim();
  if (!raw) return "/";
  const normalized = raw.replace(/\/+$/, "");
  return normalized || "/";
};

const CHAT_SESSION_SEARCH_LIMIT = 12;
const CHAT_SESSION_PREVIEW_LIMIT = 3;
const CHAT_SESSION_MORE_STEP = 5;
const THEME_STORAGE_KEY = "zeqi-webapp-theme";
const ACTIVE_CHANNEL_STORAGE_KEY = "zeqi:activeChannelId";
const ACTIVE_SUBCHANNEL_STORAGE_KEY = "zeqi:activeSubchannelId";
const GUIDE_TOUR_SEEN_STORAGE_KEY = "zeqi:guide-tour-seen:v1";
const CHANNEL_LABELS = {
  main: "Main",
  email: "Email",
  integrations: "Integrations",
  "paid-ads": "Paid ads",
  "meta-ads": "Meta Ads",
  "google-ads": "Google Ads",
  "tiktok-ads": "TikTok Ads",
  social: "Social Media",
  "content-cal": "Content calendar",
  seo: "SEO / AEO",
  pdp: "PDP",
  google: "Google",
  "google-search-console": "Google search console",
  "google-merchant-center": "Google merchant center",
};

const GUIDE_TOUR_STEPS = [
  {
    target: "integrations",
    title: "Connect your marketing tools",
    desc: "Start here. Link your Shopify store, Meta Ads, Google Analytics, Search Console, and TikTok in a few clicks. Zeqi pulls your data automatically once connected. The more tools you connect, the more complete your intelligence becomes.",
    position: "right",
  },
  {
    target: "costs",
    title: "Enter your real costs",
    desc: "Tell Zeqi what your products actually cost to make and what you spend monthly on operations. This is critical — without real cost data, margin calculations and profitability assessments will be incomplete. Most merchants skip this and wonder why their numbers feel off.",
    position: "right",
  },
  {
    target: "reports",
    title: "Your raw data, organized",
    desc: "Once your tools are connected, Zeqi generates 7 reports from your live data: store performance, ad campaigns, search visibility, merchant trust, revenue reality, target audience, and a content calendar. These are the facts — no interpretation yet, just what happened.",
    position: "left",
  },
  {
    target: "assessments",
    title: "Zeqi interprets what it all means",
    desc: "7 assessments read your reports and tell you what matters. Each one has a health indicator — green means healthy, amber needs attention, red is critical. This is the layer where raw numbers become actionable insights. Think of it as your AI analyst reviewing every report and flagging what you should care about.",
    position: "left",
  },
  {
    target: "biz-assessment",
    title: "Your daily business overview",
    desc: "The Daily Overview is the crown jewel. It reads all your assessments and synthesizes them into one executive view — covering store snapshot, acquisition and ads, revenue and retention. A fresh snapshot of your business every day, with everything that matters in one place.",
    position: "left",
  },
  {
    target: "main-channel",
    title: "Talk to your AI marketing director",
    desc: "The Main channel is where you chat with Zeqi about anything. Ask about your ads, your SEO, your revenue — Zeqi pulls from all your reports and assessments to answer. Every conversation is automatically logged to the right topic channel (Paid Ads, Email, SEO, etc.) so nothing gets lost.",
    position: "right",
  },
  {
    target: "channels-label",
    title: "Every conversation, auto-organized",
    desc: "You never need to switch channels manually. Just talk in Main and Zeqi sorts it. Click any channel to see the full history of that topic — every discussion about email marketing, every paid ads conversation, all in one place. Like having department heads who remember every meeting.",
    position: "right",
  },
  {
    target: "chat-input",
    title: "Ask anything, get data-backed answers",
    desc: "Type any question and Zeqi responds with real numbers from your connected tools. You'll see inline metric cards, confidence levels, and action chips that link directly to relevant reports. Try suggested questions above the input bar to get started quickly.",
    position: "top",
  },
  {
    target: "chat-divider",
    title: "View reports while you chat",
    desc: "When you open a report or assessment, it appears above your chat in a split view. Drag the handle to resize, use the arrows to expand either section, or press X to close. You can read a report and ask Zeqi questions about it at the same time — no tab switching, no lost context.",
    position: "top",
  },
  {
    target: "task-agent",
    title: "Every decision gets tracked",
    desc: "When you and Zeqi agree on an action — \"let's launch a retargeting campaign\" — it gets logged here automatically. The Active tab shows what's pending, grouped by channel. The Log tab shows what was completed and when. This is your accountability layer — nothing falls through the cracks.",
    position: "left",
  },
  {
    target: "brand-kb",
    title: "Teach Zeqi your brand",
    desc: "Upload your logo, define your tone of voice, and store important documents. The more Zeqi knows about your brand, the better its content suggestions, ad copy ideas, and strategic recommendations become. This is how Zeqi goes from generic AI to your AI.",
    position: "left",
  },
];

const getRouteScopedChannelId = (pathname, activeChannelId) => {
  if (pathname === "/integrations") return "integrations";
  return pathname === "/" ? activeChannelId || "main" : "main";
};

const formatSessionTimestamp = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  if (sameDay) {
    return date.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    });
  }

  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
};

const hasVisibleChatContent = (session) => {
  const messageCount = Number(session?.messageCount || 0);
  if (messageCount > 0) return true;

  const preview = String(session?.preview || "").trim().toLowerCase();
  if (!preview || preview === "no messages yet") return false;
  return true;
};


const AppShell = ({ children }) => {
  const { user } = useAuthUser();
  const { t } = useLocalization();
  const { shopData, setShopData, refreshShops } = useShop();
  const supportCenter = useSupportCenter();
  const navigate = useNavigate();
  const location = useLocation();
  const normalizedPath = useMemo(
    () => normalizePathname(location.pathname),
    [location.pathname],
  );
  const rightRailRef = useRef(null);
  const userId = user?.userId || user?._id || user?.id || "";

  const authToken = user?.token;

  const contextHeaders = authToken
    ? { headers: { Authorization: `Bearer ${authToken}` } }
    : undefined;

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sideRailExpanded, setSideRailExpanded] = useState(true);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [rightRailCollapsed, setRightRailCollapsed] = useState(false);
  const [chatSessions, setChatSessions] = useState([]);
  const [chatSearch, setChatSearch] = useState("");
  const [visibleChatLimit, setVisibleChatLimit] = useState(CHAT_SESSION_PREVIEW_LIMIT);
  const [activeChatSessionId, setActiveChatSessionId] = useState("");
  const [activeChannelId, setActiveChannelId] = useState(
    normalizedPath === "/" ? "main" : "",
  );
  const [activeSubchannelId, setActiveSubchannelId] = useState(() => {
    if (typeof window === "undefined") return "";
    try {
      return window.sessionStorage.getItem(ACTIVE_SUBCHANNEL_STORAGE_KEY) || "";
    } catch {
      return "";
    }
  });
  const [themeMode, setThemeMode] = useState(() => {
    if (typeof window === "undefined") return "dark";
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === "dark" || stored === "light") return stored;
    return window.matchMedia?.("(prefers-color-scheme: light)")?.matches
      ? "light"
      : "dark";
  });
  const [guideTourOpen, setGuideTourOpen] = useState(false);
  const [guideTourStep, setGuideTourStep] = useState(0);
  const [pendingGuideTourStart, setPendingGuideTourStart] = useState(false);

  const { data: notificationsData, refetch: refetchNotifications } = useQuery(
    GET_NOTIFICATIONS,
    {
      skip: !authToken,
      context: contextHeaders,
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "cache-first",
    },
  );

  const [markNotificationRead] = useMutation(MARK_NOTIFICATION_READ);

  const { data: shopDataResponse } = useQuery(
    GET_SHOP_DATA_FOR_RECOMMENDATION,
    {
      skip: !authToken || Boolean(shopData),
      context: contextHeaders,
      fetchPolicy: "cache-first",
      nextFetchPolicy: "cache-first",
    },
  );

  const { data: progressData } = useQuery(GET_PRIMARY_TASK_PROGRESS, {
    skip: !authToken,
    context: contextHeaders,
    fetchPolicy: "cache-first",
    nextFetchPolicy: "cache-first",
  });

  // Migrated from deprecated onCompleted - use useEffect to handle side effects
  useEffect(() => {
    const shop = shopDataResponse?.getShopDataForRecommendation?.shop;
    if (shop) {
      setShopData(shop);
    }
  }, [shopDataResponse, setShopData]);

  useEffect(() => {
    if (authToken) {
      refreshShops();
    }
  }, [authToken, refreshShops]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.dataset.theme = themeMode;
    document.documentElement.style.colorScheme = themeMode;
    window.localStorage.setItem(THEME_STORAGE_KEY, themeMode);
  }, [themeMode]);

  // const { data: aiGreetingData } = useQuery(
  //   GET_AI_GREETING,
  //   {
  //     skip: !authToken,
  //     context: contextHeaders,
  //     fetchPolicy: "cache-and-network",
  //   },
  // );

  const unreadCount = notificationsData?.myNotifications?.unreadCount ?? 0;
  const notifications = notificationsData?.myNotifications?.items ?? [];
  const isChannelShell = normalizedPath === "/";

  const fallbackName = t("GreetingSection.fallbackName");
  const ownerName = shopData?.shopOwnerName || shopData?.name || fallbackName;

  const aiGreetingText = "";

  const firstName = useMemo(() => {
    const raw = typeof ownerName === "string" ? ownerName.trim() : "";
    if (!raw) return fallbackName;
    const first = raw.split(/\s+/)[0];
    return first || fallbackName;
  }, [ownerName, fallbackName]);

  const greetingText = t("GreetingSection.greeting", { name: firstName });

  const handleProfileClick = () => navigate("/settings");
  const handleLogoClick = () => navigate("/");

  const toggleDrawer = useCallback(() => {
    setDrawerOpen((prev) => !prev);
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  const toggleSideRail = useCallback(() => {
    setSideRailExpanded((prev) => !prev);
  }, []);

  const toggleNotifications = useCallback(() => {
    setNotificationsOpen((prev) => !prev);
  }, []);

  const toggleThemeMode = useCallback(() => {
    setThemeMode((prev) => (prev === "dark" ? "light" : "dark"));
  }, []);

  const closeGuideTour = useCallback(() => {
    setGuideTourOpen(false);
    setPendingGuideTourStart(false);
    try {
      window.localStorage.setItem(GUIDE_TOUR_SEEN_STORAGE_KEY, "true");
    } catch {
      // ignore
    }
  }, []);

  const openAtStep = useCallback((stepIndex) => {
    setGuideTourStep(stepIndex);
    setGuideTourOpen(true);
  }, []);

  const startGuideTour = useCallback(() => {
    if (guideTourOpen || pendingGuideTourStart) return;
    setGuideTourStep(0);
    setPendingGuideTourStart(true);
    window.dispatchEvent(
      new CustomEvent("zeqi:set-active-channel", {
        detail: { channelId: "main", subchannelId: "" },
      }),
    );
    if (normalizedPath !== "/") {
      navigate("/");
      return;
    }
    window.dispatchEvent(new CustomEvent("zeqi:tour-open-assessment"));
  }, [guideTourOpen, navigate, normalizedPath, pendingGuideTourStart]);

  useEffect(() => {
    if (!pendingGuideTourStart || normalizedPath !== "/") return undefined;
    window.dispatchEvent(new CustomEvent("zeqi:tour-open-assessment"));
    const timeoutId = window.setTimeout(() => {
      setGuideTourOpen(true);
      setPendingGuideTourStart(false);
    }, 500);
    return () => window.clearTimeout(timeoutId);
  }, [normalizedPath, pendingGuideTourStart]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleStartTour = () => startGuideTour();
    window.addEventListener("zeqi:start-guide-tour", handleStartTour);
    return () =>
      window.removeEventListener("zeqi:start-guide-tour", handleStartTour);
  }, [startGuideTour]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const seen = window.localStorage.getItem(GUIDE_TOUR_SEEN_STORAGE_KEY) === "true";
    if (seen || pendingGuideTourStart || guideTourOpen) return undefined;
    const timeoutId = window.setTimeout(() => {
      startGuideTour();
    }, 700);
    return () => window.clearTimeout(timeoutId);
  }, [guideTourOpen, pendingGuideTourStart, startGuideTour]);

  useEffect(() => {
    if (!guideTourOpen) return;
    window.dispatchEvent(new CustomEvent("zeqi:tour-open-assessment"));
  }, [guideTourOpen, guideTourStep]);

  const loadChatSessions = useCallback(async () => {
    const effectiveChannelId = getRouteScopedChannelId(
      normalizedPath,
      activeChannelId,
    );
    if (!authToken || !userId || !effectiveChannelId) {
      setChatSessions([]);
      return;
    }

    try {
      const response = await getChannelChatSessions({
        userId,
        channelId: effectiveChannelId,
        scope: effectiveChannelId === "main" ? "all" : "channel",
        limit: 50,
        authToken,
      });
      setChatSessions(
        Array.isArray(response?.sessions) ? response.sessions : [],
      );
    } catch (err) {
      console.warn(
        "[appshell] unable to load channel chat sessions",
        err?.message || err,
      );
    }
  }, [activeChannelId, authToken, normalizedPath, userId]);

  useEffect(() => {
    loadChatSessions();
  }, [loadChatSessions]);

  useEffect(() => {
    const refreshHandler = () => {
      loadChatSessions();
    };
    const sessionChangedHandler = (event) => {
      setActiveChatSessionId(event?.detail?.sessionId || "");
    };

    window.addEventListener("zeqi:channel-chat-refresh-sessions", refreshHandler);
    window.addEventListener("zeqi:chat-session-changed", sessionChangedHandler);
    return () => {
      window.removeEventListener(
        "zeqi:channel-chat-refresh-sessions",
        refreshHandler,
      );
      window.removeEventListener(
        "zeqi:chat-session-changed",
        sessionChangedHandler,
      );
    };
  }, [loadChatSessions]);

  // Ensure right rail is not collapsed when on the Overview page
  useEffect(() => {
    setRightRailCollapsed(false);
  }, [normalizedPath]);

  const handleNotificationClick = useCallback(
    async (notification) => {
      if (!authToken) return;
      try {
        if (!notification.read) {
          await markNotificationRead({
            variables: { id: notification.id },
            context: contextHeaders,
          });
          await refetchNotifications?.();
        }
      } catch (err) {
        console.error("markNotificationRead.error", err);
      }
      if (notification.type === "REPORT") {
        navigate("/reports");
      } else if (notification.type === "FOUNDATION_STAGE") {
        navigate("/");
      } else {
        navigate("/notifications");
      }
    },
    [
      authToken,
      contextHeaders,
      markNotificationRead,
      navigate,
      refetchNotifications,
    ],
  );

  const navItems = useMemo(
    () => [
      { path: "/", label: t("ZeqiMainTab.assessment") || "Assessment" },
      { path: "/key-focus", label: t("ZeqiMainTab.keyFocus") || "Key Focus" },
    ],
    [t],
  );

  const handleMainChannelClick = useCallback(() => {
    setActiveChannelId("main");
    setActiveSubchannelId("");
    try {
      window.sessionStorage.setItem(ACTIVE_CHANNEL_STORAGE_KEY, "main");
      window.sessionStorage.removeItem(ACTIVE_SUBCHANNEL_STORAGE_KEY);
    } catch {
      // ignore
    }
    window.dispatchEvent(
      new CustomEvent("zeqi:channel-changed", {
        detail: { channelId: "main", subchannelId: "" },
      }),
    );
    if (normalizedPath === "/") {
      window.dispatchEvent(new CustomEvent("zeqi:overview-reclick"));
      return;
    }
    navigate("/");
  }, [navigate, normalizedPath]);

  const handleChannelClick = useCallback(
    (channelId, subchannelId = "") => {
      setActiveChannelId(channelId);
      setActiveSubchannelId(subchannelId);
      try {
        window.sessionStorage.setItem(ACTIVE_CHANNEL_STORAGE_KEY, channelId);
        if (subchannelId) {
          window.sessionStorage.setItem(
            ACTIVE_SUBCHANNEL_STORAGE_KEY,
            subchannelId,
          );
        } else {
          window.sessionStorage.removeItem(ACTIVE_SUBCHANNEL_STORAGE_KEY);
        }
      } catch {
        // ignore
      }
      window.dispatchEvent(
        new CustomEvent("zeqi:channel-changed", {
          detail: { channelId, subchannelId },
        }),
      );
      if (normalizedPath !== "/") {
        navigate("/");
      }
    },
    [navigate, normalizedPath],
  );

  const drawerItems = useMemo(
    () => [
      // {
      //   label: t("DrawerSection.shopReports"),
      //   onClick: () => {
      //     navigate("/reports");
      //     closeDrawer();
      //   },
      // },
      {
        label: "# Main",
        onClick: () => {
          handleMainChannelClick();
          closeDrawer();
        },
      },
      // {
      //   label: "# Email",
      //   onClick: () => {
      //     handleChannelClick("email");
      //     closeDrawer();
      //   },
      // },
      // {
      //   label: "# Paid ads",
      //   onClick: () => {
      //     handleChannelClick("paid-ads");
      //     closeDrawer();
      //   },
      // },
      // {
      //   label: "# Social Media",
      //   onClick: () => {
      //     handleChannelClick("social");
      //     closeDrawer();
      //   },
      // },
      // {
      //   label: "# Content calendar",
      //   onClick: () => {
      //     handleChannelClick("content-cal");
      //     closeDrawer();
      //   },
      // },
      // {
      //   label: "# SEO / AEO",
      //   onClick: () => {
      //     handleChannelClick("seo");
      //     closeDrawer();
      //   },
      // },
      // {
      //   label: "# Google",
      //   onClick: () => {
      //     handleChannelClick("google");
      //     closeDrawer();
      //   },
      // },
      {
        label: t("DrawerSection.productCosts"),
        onClick: () => {
          navigate("/product-costs");
          closeDrawer();
        },
      },
      {
        label: t("DrawerSection.operatingCosts"),
        onClick: () => {
          navigate("/operating-costs");
          closeDrawer();
        },
      },
      {
        label: t("DrawerSection.integrations") || "Integrations",
        onClick: () => {
          navigate("/integrations");
          closeDrawer();
        },
      },
      {
        type: "separator",
      },
      {
        label: t("DrawerSection.contactSupport"),
        onClick: () => {
          closeDrawer();
          supportCenter?.openSupport?.();
        },
      },
      // {
      //   label: t("DrawerSection.reportBug"),
      //   onClick: () => {
      //     closeDrawer();
      //     supportCenter?.openReportBug?.();
      //   },
      // },
      // {
      //   label: t("DrawerSection.requestFeature"),
      //   onClick: () => {
      //     closeDrawer();
      //     supportCenter?.openRequestFeature?.();
      //   },
      // },
      // {
      //   label: t("DrawerSection.helpCenter"),
      //   onClick: () => {
      //     closeDrawer();
      //     navigate("/help-center");
      //   },
      // },
    ],
    [
      t,
      navigate,
      closeDrawer,
      supportCenter,
      handleChannelClick,
      handleMainChannelClick,
    ],
  );

  const channelItems = useMemo(
    () => [
      {
        id: "main",
        label: "Main",
        onClick: handleMainChannelClick,
        isActive: () =>
          normalizedPath === "/" &&
          activeChannelId === "main" &&
          !activeSubchannelId,
      },
      // {
      //   id: "email",
      //   label: "Email",
      //   onClick: undefined,
      //   disabled: true,
      //   badge: "Coming soon",
      //   isActive: () =>
      //     normalizedPath === "/" &&
      //     activeChannelId === "email" &&
      //     !activeSubchannelId,
      // },
      // {
      //   id: "paid-ads",
      //   label: "Paid ads",
      //   onClick: () => handleChannelClick("paid-ads"),
      //   isActive: () => normalizedPath === "/" && activeChannelId === "paid-ads",
      //   children: [
      //     {
      //       id: "meta-ads",
      //       label: "Meta Ads",
      //       onClick: () => handleChannelClick("paid-ads", "meta-ads"),
      //       isActive: () =>
      //         normalizedPath === "/" && activeSubchannelId === "meta-ads",
      //     },
      //   ],
      // },
      // {
      //   id: "social",
      //   label: "Social Media",
      //   onClick: () => handleChannelClick("social"),
      //   isActive: () =>
      //     normalizedPath === "/" &&
      //     activeChannelId === "social" &&
      //     !activeSubchannelId,
      // },
      // {
      //   id: "content-cal",
      //   label: "Content calendar",
      //   onClick: () => handleChannelClick("content-cal"),
      //   isActive: () =>
      //     normalizedPath === "/" &&
      //     activeChannelId === "content-cal" &&
      //     !activeSubchannelId,
      // },
      // {
      //   id: "seo",
      //   label: "SEO / AEO",
      //   onClick: () => handleChannelClick("seo"),
      //   isActive: () =>
      //     normalizedPath === "/" &&
      //     activeChannelId === "seo" &&
      //     !activeSubchannelId,
      // },
      // {
      //   id: "pdp",
      //   label: "PDP",
      //   onClick: () => handleChannelClick("pdp"),
      //   isActive: () =>
      //     normalizedPath === "/" &&
      //     activeChannelId === "pdp" &&
      //     !activeSubchannelId,
      // },
      // {
      //   id: "google",
      //   label: "Google",
      //   onClick: () => handleChannelClick("google"),
      //   isActive: () => normalizedPath === "/" && activeChannelId === "google",
      //   children: [
      //     {
      //       id: "google-search-console",
      //       label: "Google Search Console",
      //       onClick: () =>
      //         handleChannelClick("google", "google-search-console"),
      //       isActive: () =>
      //         normalizedPath === "/" &&
      //         activeSubchannelId === "google-search-console",
      //     },
      //     {
      //       id: "google-merchant-center",
      //       label: "Google Merchant Center",
      //       onClick: () =>
      //         handleChannelClick("google", "google-merchant-center"),
      //       isActive: () =>
      //         normalizedPath === "/" &&
      //         activeSubchannelId === "google-merchant-center",
      //     },
      //   ],
      // },
    ],
    [
      activeChannelId,
      activeSubchannelId,
      handleChannelClick,
      handleMainChannelClick,
      normalizedPath,
    ],
  );

  const activeChannelLabel = useMemo(() => {
    if (activeSubchannelId && CHANNEL_LABELS[activeSubchannelId]) {
      return CHANNEL_LABELS[activeSubchannelId];
    }
    return CHANNEL_LABELS[activeChannelId] || "Main";
  }, [activeChannelId, activeSubchannelId]);

  const costItems = useMemo(
    () => [
      {
        label: t("DrawerSection.productCosts"),
        onClick: () => navigate("/product-costs"),
        icon: Tag,
        isActive: () => normalizedPath === "/product-costs",
      },
      {
        label: t("DrawerSection.operatingCosts"),
        onClick: () => navigate("/operating-costs"),
        icon: Receipt,
        isActive: () => normalizedPath === "/operating-costs",
      },
    ],
    [navigate, normalizedPath, t],
  );

  const footerSideRailItems = useMemo(
    () => [
      {
        label: t("DrawerSection.integrations") || "Integrations",
        id: "integrations",
        onClick: () => navigate("/integrations"),
        icon: Link2,
        isActive: () => normalizedPath === "/integrations",
      },
      {
        label: t("AppShell.userSettings") || "Settings",
        onClick: handleProfileClick,
        icon: UserCircle,
        isActive: () => normalizedPath === "/settings",
      },
      {
        label: "Guide tour",
        onClick: startGuideTour,
        icon: HelpCircle,
      },
      {
        label: t("DrawerSection.contactSupport"),
        onClick: () => supportCenter?.openSupport?.(),
        icon: Headset,
      },
    ],
    [handleProfileClick, navigate, normalizedPath, startGuideTour, supportCenter, t],
  );

  const hiddenGreetingPaths = new Set([
    "/",
    "/notifications",
    "/settings",
    "/reports",
    "/chat",
    "/product-costs",
    "/operating-costs",
    "/strategy-report",
    "/billing",
    "/help-center",
    "/integrations",
    "/brand",
  ]);

  const showGreeting = !hiddenGreetingPaths.has(normalizedPath);

  const hiddenNavPaths = new Set([
    "/",
    "/notifications",
    "/settings",
    "/reports",
    "/chat",
    "/help-center",
    "/product-costs",
    "/operating-costs",
    "/strategy-report",
    "/billing",
    "/integrations",
  ]);

  const showNavItems = !hiddenNavPaths.has(normalizedPath);
  const isChatPage = normalizedPath === "/chat";

  // Check if Chat tab is active on HomePage
  const [isChatTabActive, setIsChatTabActive] = useState(
    normalizedPath === "/",
  );

  useEffect(() => {
    if (normalizedPath !== "/") {
      setIsChatTabActive(false);
      return;
    }

    const chatLabel = t("ZeqiMainTab.chat") || "Chat";
    let bodyObserver = null;
    let attrObserver = null;

    const checkChatTab = () => {
      try {
        const contentWrap = document.querySelector("[data-active-tab]");
        if (contentWrap) {
          const activeTab = contentWrap.getAttribute("data-active-tab");
          const isChat = activeTab === chatLabel;
          setIsChatTabActive(isChat);
          return true;
        }
      } catch {
        setIsChatTabActive(false);
      }
      return false;
    };

    // Initial check with a small delay to allow HomePage to render
    const initialTimeout = setTimeout(() => {
      checkChatTab();
    }, 50);

    // Set up observer on document.body to watch for the element being added
    bodyObserver = new MutationObserver(() => {
      const contentWrap = document.querySelector("[data-active-tab]");
      if (contentWrap) {
        checkChatTab();
        // Start observing the specific element for attribute changes
        if (!attrObserver) {
          attrObserver = new MutationObserver(checkChatTab);
          attrObserver.observe(contentWrap, {
            attributes: true,
            attributeFilter: ["data-active-tab"],
          });
        }
      }
    });

    // Start observing the body for when the HomePage renders
    bodyObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      clearTimeout(initialTimeout);
      if (bodyObserver) {
        bodyObserver.disconnect();
      }
      if (attrObserver) {
        attrObserver.disconnect();
      }
    };
  }, [normalizedPath, t]);

  useEffect(() => {
    if (normalizedPath === "/" && !activeChannelId) {
      let stored = "main";
      try {
        stored = window.sessionStorage.getItem(ACTIVE_CHANNEL_STORAGE_KEY) || "main";
      } catch {
        // ignore
      }
      setActiveChannelId(stored);
      setActiveSubchannelId("");
      try {
        window.sessionStorage.setItem(ACTIVE_CHANNEL_STORAGE_KEY, stored);
        window.sessionStorage.removeItem(ACTIVE_SUBCHANNEL_STORAGE_KEY);
      } catch {
        // ignore
      }
      return;
    }
    if (normalizedPath !== "/") {
      setActiveChannelId("");
      setActiveSubchannelId("");
    }
  }, [activeChannelId, normalizedPath]);

  useEffect(() => {
    if (!isChannelShell) return;
    setActiveChatSessionId("");
  }, [activeChannelId, isChannelShell]);

  useEffect(() => {
    const handler = (event) => {
      const channelId = event?.detail?.channelId;
      const subchannelId = event?.detail?.subchannelId || "";
      if (!channelId) return;
      setActiveChannelId(channelId);
      setActiveSubchannelId(subchannelId);
      try {
        window.sessionStorage.setItem(ACTIVE_CHANNEL_STORAGE_KEY, channelId);
        if (subchannelId) {
          window.sessionStorage.setItem(
            ACTIVE_SUBCHANNEL_STORAGE_KEY,
            subchannelId,
          );
        } else {
          window.sessionStorage.removeItem(ACTIVE_SUBCHANNEL_STORAGE_KEY);
        }
      } catch {
        // ignore
      }
      if (normalizedPath !== "/") {
        navigate("/");
      }
    };
    window.addEventListener("zeqi:set-active-channel", handler);
    return () => window.removeEventListener("zeqi:set-active-channel", handler);
  }, [navigate, normalizedPath]);

  const hideRightRail = isChatPage;
  const isOverviewPage = normalizedPath === "/";

  const [activeHomeTab, setActiveHomeTab] = useState("");
  const [comingSoonFeature, setComingSoonFeature] = useState(null);

  const promotedRaw = progressData?.getPrimaryTaskProgress?.promoted;
  const isPromoted =
    promotedRaw === true ||
    promotedRaw === "true" ||
    promotedRaw === 1 ||
    promotedRaw === "1";
  const progressCluster = String(
    progressData?.getPrimaryTaskProgress?.cluster_flag || "",
  )
    .toLowerCase()
    .trim();
  const isPostOnboarding =
    progressCluster.includes("foundation") ||
    progressCluster.includes("funnel") ||
    isPromoted;

  useEffect(() => {
    if (normalizedPath !== "/") {
      setActiveHomeTab("");
      return;
    }
    const handler = (e) => {
      setActiveHomeTab(e?.detail?.tab || "");
    };
    window.addEventListener("zeqi:tab-changed", handler);
    return () => window.removeEventListener("zeqi:tab-changed", handler);
  }, [normalizedPath]);

  const rightRailNavItems = useMemo(() => {
    const assessmentLabel = t("ZeqiMainTab.assessment") || "Assessment";
    const strategyLabel = t("ZeqiMainTab.strategy") || "Strategy";
    const reportsLabel = t("ZeqiMainTab.reports") || "Reports";
    const executionLabel = t("ZeqiMainTab.keyFocus") || "Execution";
    return [
      {
        label: reportsLabel,
        tab: reportsLabel,
        path: "/reports",
        isComingSoon: false,
        isExecutionLocked: false,
        iconType: "reports",
      },
      {
        label: assessmentLabel,
        tab: assessmentLabel,
        path: "/assessment",
        isComingSoon: false,
        isExecutionLocked: false,
        iconType: "assessment",
      },
      {
        label: strategyLabel,
        tab: strategyLabel,
        path: "/strategy-report",
        isComingSoon: true,
        isExecutionLocked: false,
        iconType: "strategy",
      },
      {
        label: executionLabel,
        tab: executionLabel,
        path: "/key-focus",
        isComingSoon: false,
        isExecutionLocked: true,
        iconType: "execution",
      },
    ];
  }, [t]);

  const handleRightRailNav = useCallback(
    (item) => {
      const isExecutionDisabled = item.isExecutionLocked && isPostOnboarding;
      const isDisabled = item.isComingSoon || isExecutionDisabled;
      if (isDisabled) {
        setComingSoonFeature(item.tab);
        return;
      }
      navigate(item.path);
    },
    [normalizedPath, navigate, isPostOnboarding],
  );

  const emptyHeaderPages = useMemo(
    () =>
      new Set([
        "/product-costs",
        "/operating-costs",
        "/settings",
        "/integrations",
      ]),
    [],
  );
  const showHeaderGreeting =
    !emptyHeaderPages.has(normalizedPath) &&
    (normalizedPath !== "/" || isChatTabActive);

  const filteredChatSessions = useMemo(() => {
    const query = chatSearch.trim().toLowerCase();
    const base = (Array.isArray(chatSessions) ? chatSessions : []).filter(
      hasVisibleChatContent,
    );
    if (!query) return base.slice(0, CHAT_SESSION_SEARCH_LIMIT);
    return base
      .filter((session) => {
        const channelLabel = CHANNEL_LABELS[session?.channelId] || "";
        const subchannelLabel = CHANNEL_LABELS[session?.subchannelId] || "";
        const haystack = [
          session.title,
          session.preview,
          session.reportId,
          session.channelId,
          session.subchannelId,
          channelLabel,
          subchannelLabel,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(query);
      })
      .slice(0, CHAT_SESSION_SEARCH_LIMIT);
  }, [chatSearch, chatSessions]);

  useEffect(() => {
    setVisibleChatLimit(CHAT_SESSION_PREVIEW_LIMIT);
  }, [chatSearch]);

  const previewChatSessions = useMemo(
    () => filteredChatSessions.slice(0, visibleChatLimit),
    [filteredChatSessions, visibleChatLimit],
  );

  const canShowMoreChatSessions =
    filteredChatSessions.length > visibleChatLimit;

  const formatChatSessionMeta = useCallback(
    (session) => {
      const label =
        CHANNEL_LABELS[session?.subchannelId] ||
        CHANNEL_LABELS[session?.channelId] ||
        "";
      const timestamp = formatSessionTimestamp(
        session?.lastMessageAt || session?.lastActivityAt || session?.startedAt,
      );
      if (label && timestamp) return `# ${label} · ${timestamp}`;
      if (label) return `# ${label}`;
      return timestamp;
    },
    [],
  );

  const openChatSession = useCallback(
    (sessionId) => {
      if (!sessionId) return;

      const chatLabel = t("ZeqiMainTab.chat") || "Chat";
      try {
        sessionStorage.setItem("zeqi:pendingChatSession", sessionId);
        sessionStorage.setItem("zeqi:pendingTab", chatLabel);
        sessionStorage.setItem(ACTIVE_CHANNEL_STORAGE_KEY, "main");
        sessionStorage.removeItem(ACTIVE_SUBCHANNEL_STORAGE_KEY);
      } catch {
        // ignore
      }

      setActiveChannelId("main");
      setActiveSubchannelId("");
      setActiveChatSessionId(sessionId);
      window.dispatchEvent(
        new CustomEvent("zeqi:channel-changed", {
          detail: { channelId: "main", subchannelId: "" },
        }),
      );
      window.dispatchEvent(
        new CustomEvent("zeqi:open-chat-session", {
          detail: { sessionId },
        }),
      );

      window.dispatchEvent(
        new CustomEvent("zeqi:switch-tab", {
          detail: { tab: chatLabel },
        }),
      );

      if (normalizedPath !== "/") {
        navigate("/");
      }
    },
    [navigate, normalizedPath, t],
  );

  const handleNewChat = useCallback(() => {
    if (!userId) return;
    const nextSessionId = createChannelChatSessionId(userId, "main");
    const chatLabel = t("ZeqiMainTab.chat") || "Chat";
    try {
      window.sessionStorage.setItem("zeqi:pendingChatSession", nextSessionId);
      window.sessionStorage.setItem("zeqi:pendingTab", chatLabel);
      window.sessionStorage.setItem(ACTIVE_CHANNEL_STORAGE_KEY, "main");
      window.sessionStorage.removeItem(ACTIVE_SUBCHANNEL_STORAGE_KEY);
    } catch {
      // ignore
    }
    setActiveChatSessionId(nextSessionId);
    setActiveChannelId("main");
    setActiveSubchannelId("");
    window.dispatchEvent(
      new CustomEvent("zeqi:channel-changed", {
        detail: { channelId: "main", subchannelId: "" },
      }),
    );
    window.dispatchEvent(
      new CustomEvent("zeqi:open-chat-session", {
        detail: { sessionId: nextSessionId, isNew: true },
      }),
    );

    window.dispatchEvent(
      new CustomEvent("zeqi:switch-tab", { detail: { tab: chatLabel } }),
    );

    if (normalizedPath !== "/") {
      navigate("/");
    }
  }, [navigate, normalizedPath, t, userId]);

  return (
    <GuideTourContext.Provider value={{ openAtStep }}>
    <div
      className={`${styles.appShell} ${
        sideRailExpanded ? styles.appShellExpanded : styles.appShellCollapsed
      } ${hideRightRail ? styles.hideRightRail : ""} ${rightRailCollapsed ? styles.rightRailCollapsed : ""}`}
    >
      <AppSideRail
        channelItems={channelItems}
        costItems={costItems}
        footerItems={footerSideRailItems}
        shopName={shopData?.name || ownerName}
        handleLogoClick={handleLogoClick}
        handleNewChat={handleNewChat}
        chatSearch={chatSearch}
        setChatSearch={setChatSearch}
        previewChatSessions={previewChatSessions}
        activeChatSessionId={activeChatSessionId}
        openChatSession={openChatSession}
        canShowMoreChatSessions={canShowMoreChatSessions}
        handleShowMoreChat={() => setVisibleChatLimit((n) => n + CHAT_SESSION_MORE_STEP)}
        formatSessionTimestamp={formatSessionTimestamp}
        formatChatSessionMeta={formatChatSessionMeta}
      />
      <AppShellHeader
        normalizedPath={normalizedPath}
        showHeaderGreeting={showHeaderGreeting}
        activeChannelLabel={activeChannelLabel}
        handleLogoClick={handleLogoClick}
        toggleDrawer={toggleDrawer}
        themeMode={themeMode}
        toggleThemeMode={toggleThemeMode}
        unreadCount={unreadCount}
        handleProfileClick={handleProfileClick}
        ownerName={ownerName}
        t={t}
      />

      <div className={styles.innerWrapper}>
        {/* showGreeting && (
          <section
            className={`${styles.greetingSection} ${styles.greetingSectionMobile}`}
          >
            <div className={styles.greetingArea}>
              <p className={styles.greetingHeadline}>{greetingText}</p>
              <p className={styles.greetingSubtitle}>{aiGreetingText}</p>
            </div>
          </section>
        ) */}
        {/*
        showNavItems && (
          <nav className={styles.nav}>
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  isActive
                    ? `${styles.navItem} ${styles.navItemActive}`
                    : styles.navItem
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        ) */}
      </div>

      {drawerOpen && (
        <div
          className={styles.drawerBackdrop}
          onClick={closeDrawer}
          role="presentation"
        >
          <div
            className={styles.drawer}
            onClick={(event) => event.stopPropagation()}
          >
            <div className={styles.drawerHeader}>
              <p className={styles.drawerTitle}>
                {t("AppShell.menuTitle") || "Menu"}
              </p>
              <button
                type="button"
                className={styles.closeDrawer}
                onClick={closeDrawer}
                aria-label={t("AppShell.closeMenu") || "Close menu"}
              >
                ×
              </button>
            </div>

            <div className={styles.drawerBody}>
              {drawerItems.map((item, index) => {
                if (item.type === "separator") {
                  return (
                    <div
                      key={`separator-${index}`}
                      className={styles.drawerItemSeparator}
                    />
                  );
                }

                return (
                  <button
                    key={item.label}
                    type="button"
                    className={styles.drawerItem}
                    onClick={item.onClick}
                  >
                    {item.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <main className={styles.content}>
        <div key={location.pathname} className={styles.pageTransition}>
          {children}
        </div>
      </main>

      {!hideRightRail && !rightRailCollapsed && (
        <AppRightRail
          ref={rightRailRef}
          activeChannelId={activeChannelId}
          activeSubchannelId={activeSubchannelId}
          unreadCount={unreadCount}
          notificationsOpen={notificationsOpen}
          toggleNotifications={toggleNotifications}
          notifications={notifications}
          handleNotificationClick={handleNotificationClick}
          handleViewMoreNotifications={() => navigate("/notifications")}
          rightRailNavItems={rightRailNavItems}
          isPostOnboarding={isPostOnboarding}
          isOverviewPage={isOverviewPage}
          activeHomeTab={activeHomeTab}
          handleRightRailNav={handleRightRailNav}
        />
      )}

      {/* Restore button when right rail is collapsed */}
      {/* <button
        type="button"
        onClick={() => navigate("/chat")}
        className={`${styles.chatFab} ${styles.mobileOnly}`}
        aria-label={t("AppShell.chatAriaLabel") || "Open Zeqi chat"}
      >
        <img
          src="/assets/zeqi_voice.png"
          alt=""
          className={styles.chatFabIcon}
          aria-hidden="true"
        />
      </button> */}

      {comingSoonFeature && (
        <ComingSoonModal
          feature={comingSoonFeature}
          onClose={() => setComingSoonFeature(null)}
        />
      )}
      <GuideTourOverlay
        isOpen={guideTourOpen}
        steps={GUIDE_TOUR_STEPS}
        currentStep={guideTourStep}
        onClose={closeGuideTour}
        onPrev={() => setGuideTourStep((current) => Math.max(0, current - 1))}
        onNext={() =>
          setGuideTourStep((current) => {
            if (current >= GUIDE_TOUR_STEPS.length - 1) {
              closeGuideTour();
              return current;
            }
            return current + 1;
          })
        }
      />
    </div>
    </GuideTourContext.Provider>
  );
};

export default AppShell;
